import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable, of, switchMap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';

import {
  ENDPOINTS,
  HEADERS,
} from 'src/app/core/constants/generic-headers.constants';
import { StepsService } from '../steps/steps.service';

import {
  ClientInfoResponse,
  Compatibility,
  CompatibilityApi,
  DnValidation,
  IBaseResponse,
  IContractBody,
  IContractResponse,
  IContractResponseApi,
  IDn,
  IInfoClientAvailableCredit,
  IResponseAvailableCredit,
  IResponseAvailableCreditApi,
  IResponseDnProfile,
  IResponseSaveInfoClient,
  ISaveClientInfo,
  ISaveSignature,
  IValidationDnTypeService,
  IZipCode,
  IZipCodeApi,
  OtpResponse,
  OtpResponseValidation
} from '../../interfaces/validations.interface';

import {
  BodyContractInfoAdapter,
  BodyCreditAvailableAdapter,
  BodyProfileDnAdapter,
  ClientInfoAdapter,
  CompatibilityAdapter,
  GetInfoContractAdapter,
  OtpRequestAdapter,
  OtpResponseAdapter,
  ResponseAvailableCreditAdapter,
  ResponseProfileDnAdapter,
  SaveClientInfoAdapter,
  SaveLegalsSignature,
  ValidationDnAdapter,
  validationDnTypeAdapter,
  ZipCodeAdapter
} from '../../adapters/validations.adapter';

import {
  SERVICE_TYPE,
  ServiceType,
} from 'src/app/core/constants/service-type.constant';
import { incodeLoginResponseAdapter } from '../../adapters/incode.adapter';
import {
  IIncodeStatusResponseApi,
  ILoginIncodeResponse,
  ILoginIncodeResponseApi,
} from '../../interfaces/incode.interface';
import { ValidationsServiceBase } from './validations.base';

@Injectable({
  providedIn: 'root',
})
export class ValidationsService extends ValidationsServiceBase {
  private readonly apiUrl = environment.baseUrl;
  private readonly apiUrl2 = environment.baseUrl2;
  private readonly apiUrlIncode = environment.incodeUrl;

  private readonly _http = inject(HttpClient);
  private readonly _steps = inject(StepsService);

  private readonly NO_PAYMENT_ENDPOINTS: Record<ServiceType, string> = {
    [SERVICE_TYPE.MIGRATED]: ENDPOINTS.hiring.migratedContract,
    [SERVICE_TYPE.NEW]: ENDPOINTS.hiring.newContract,
    [SERVICE_TYPE.ESIM]: ENDPOINTS.hiring.esimContract,
    [SERVICE_TYPE.PORTABILITY]: ENDPOINTS.hiring.portInForm,
  };

  private getDefaultHeaders(): HttpHeaders {
    let baseHeaders = new HttpHeaders()
      .set('Content-Type', HEADERS.contentType)
      .set('x-ord-api', HEADERS.xOrdApi)
      .set('x-ord-service', HEADERS.xOrdService);

    if (!environment.production) {
      baseHeaders = baseHeaders.set('ngrok-skip-browser-warning', 'erty');
    }

    return baseHeaders;
  }

  private getBaitHeaders(): HttpHeaders {
    return new HttpHeaders().set('x-ord-service', HEADERS.xOrdService2);
  }

  ValidateDn(dn: DnValidation): Observable<OtpResponse> {
    return this._http
      .post<OtpResponse>(
        `${this.apiUrl2}${ENDPOINTS.hiring.dnValidate2}`,
        ValidationDnAdapter(dn),
        { headers: this.getDefaultHeaders() },
      )
      .pipe(map(OtpRequestAdapter));
  }

  validateOtp(
    code: string,
    idDn: string,
    imei: string,
  ): Observable<OtpResponseValidation> {
    return this._http
      .post<
        IBaseResponse<OtpResponseValidation>
      >(`${this.apiUrl}${ENDPOINTS.hiring.otpValidate}${idDn}/validation?code=${code}`, { imei }, { headers: this.getDefaultHeaders() })
      .pipe(map(OtpResponseAdapter));
  }

  checkCompatibility(imei: string): Observable<Compatibility> {
    return this._http
      .post<
        IBaseResponse<CompatibilityApi>
      >(`${this.apiUrl}${ENDPOINTS.hiring.IMEI}`, { imei }, { headers: this.getDefaultHeaders() })
      .pipe(map(CompatibilityAdapter));
  }

  validateOtpPortability(
    code: string,
    idDn: string,
  ): Observable<OtpResponseValidation> {
    return this._http
      .post<IBaseResponse<OtpResponseValidation>>(
        `${this.apiUrl}${ENDPOINTS.hiring.otpPortability}${idDn}/portability?code=${code}`,
        {
          imei: this._steps.getImei(),
          nip: this._steps.nip,
        },
        { headers: this.getDefaultHeaders() },
      )
      .pipe(map(OtpResponseAdapter));
  }

  validateDnType(dn: string): Observable<IValidationDnTypeService> {
    return this._http
      .get<IValidationDnTypeService>(
        `${environment.baseUrlBait}/api/core/q/dn/${dn}/get/status/mbb`,
        { headers: this.getBaitHeaders() },
      )
      .pipe(map(validationDnTypeAdapter));
  }

  getClientInfo(sessionId: string): Observable<ClientInfoResponse> {
    return this._http
      .get<ClientInfoResponse>(
        `${this.apiUrl}${ENDPOINTS.hiring.clientInfo}/${sessionId}`,
        { observe: 'response' },
      )
      .pipe(
        switchMap((response: HttpResponse<ClientInfoResponse>) => {
          if (response.status === 202) {
            return throwError(() => ({
              message: 'No se ha podido obtener la información de au10tix',
              code: 202,
            }));
          }
          return of(ClientInfoAdapter(response.body));
        }),
      );
  }

  getColony(colony: string): Observable<IZipCode> {
    return this._http
      .get<IZipCodeApi>(
        `${this.apiUrl2}${ENDPOINTS.hiring.cpSearch}/${colony}/search`,
      )
      .pipe(map(ZipCodeAdapter));
  }

  profileDn(value: IDn, type: string): Observable<IResponseDnProfile> {
    return this._http
      .post<IResponseDnProfile>(
        `${this.apiUrl}${ENDPOINTS.hiring.dnProfile}${type}`,
        BodyProfileDnAdapter(value),
      )
      .pipe(map(ResponseProfileDnAdapter));
  }

  saveClientInfo(
    dataClient: ISaveClientInfo,
  ): Observable<IResponseSaveInfoClient> {
    return this._http.post<IResponseSaveInfoClient>(
      `${this.apiUrl}${ENDPOINTS.hiring.saveClientData}`,
      SaveClientInfoAdapter(dataClient),
    );
  }

  saveClientPortabilityInfo(
    dataClient: ISaveClientInfo,
  ): Observable<IResponseSaveInfoClient> {
    return this._http.post<IResponseSaveInfoClient>(
      `${this.apiUrl}${ENDPOINTS.hiring.savePortabilityClientData}`,
      SaveClientInfoAdapter(dataClient),
    );
  }

  getOffersAvailableCredit(
    code: string,
    idDn: string,
    clientInfo: IInfoClientAvailableCredit,
    classification: string,
  ): Observable<IResponseAvailableCredit> {
    const params = new HttpParams()
      .set('code', code)
      .set('classification', classification);

    return this._http
      .post<IResponseAvailableCreditApi>(
        `${this.apiUrl}${ENDPOINTS.hiring.otpValidate}${idDn}/available-credit`,
        BodyCreditAvailableAdapter(clientInfo),
        { headers: this.getDefaultHeaders(), params },
      )
      .pipe(map(ResponseAvailableCreditAdapter));
  }

  getInfoContract(
    offer: IContractBody,
    uuidPreregister: string,
  ): Observable<IContractResponse> {
    return this._http
      .post<IContractResponseApi>(
        `${this.apiUrl}/public/client/${uuidPreregister}${ENDPOINTS.hiring.contractAssign}`,
        BodyContractInfoAdapter(offer),
      )
      .pipe(map(GetInfoContractAdapter));
  }

  saveLegalsSignature(
    signature: ISaveSignature,
    uuid: string,
  ): Observable<unknown> {
    return this._http.post(
      `${this.apiUrl}${ENDPOINTS.hiring.pdfContract}${uuid}/save-signature`,
      SaveLegalsSignature(signature),
    );
  }

  viewPortability(uuidContract: string): Observable<unknown> {
    return this._http.patch(
      `${this.apiUrl}${ENDPOINTS.hiring.portInForm}`,
      { contract: uuidContract },
      { headers: this.getDefaultHeaders() },
    );
  }

  noPaymentContract(
    uuidContract: string,
    typeService: ServiceType,
  ): Observable<unknown> {
    return this._http.post(
      `${this.apiUrl}${this.NO_PAYMENT_ENDPOINTS[typeService]}`,
      { contract: uuidContract },
      { headers: this.getDefaultHeaders() },
    );
  }

  getValidateStepIncode(
    sessionId: string,
    token: string,
  ): Observable<IIncodeStatusResponseApi> {
    const headersIncode = new HttpHeaders()
      .set('x-orq-session', sessionId)
      .set('x-orq-channel', environment.incodeHeaders.channel)
      .set('Authorization', `Bearer ${token}`);

    return this._http.get<IIncodeStatusResponseApi>(
      `${this.apiUrlIncode}${ENDPOINTS.hiring.validateStepIncode}`,
      { headers: headersIncode },
    );
  }

  loginIncode(): Observable<ILoginIncodeResponse> {
    const incodeLoginHeaders = new HttpHeaders()
      .set('company', environment.incodeHeaders.company)
      .set('channel', environment.incodeHeaders.channel)
      .set('service', environment.incodeHeaders.service);

    return this._http
      .post<ILoginIncodeResponseApi>(
        `${this.apiUrlIncode}${ENDPOINTS.hiring.loginIncode}`,
        environment.incodeKeys,
        { headers: incodeLoginHeaders },
      )
      .pipe(map(incodeLoginResponseAdapter));
  }
}
